import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, Play, Sparkles, Star, Users, BookOpen, Trophy, CheckCircle, TrendingUp, Phone, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

const floatingBadges = [
  { icon: Users, label: '10K+ Students', pos: 'top-[8%] left-[-2%]', delay: 0 },
  { icon: Star, label: '4.9 Rating', pos: 'top-[62%] left-[-3%]', delay: 0.4 },
  { icon: Trophy, label: '94% Placement', pos: 'top-[12%] right-[-2%]', delay: 0.2 },
  { icon: BookOpen, label: '10+ Courses', pos: 'bottom-[14%] right-[-2%]', delay: 0.6 },
];

const words = ['Transform', 'Accelerate', 'Launch', 'Elevate'];

export default function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setWordIndex(i => (i + 1) % words.length), 2800);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-[#fffef5] to-[#f8f8f8]" />

      {/* Animated blobs */}
      <motion.div
        animate={{ scale: [1, 1.25, 1], x: [0, 30, 0], y: [0, -30, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-[5%] right-[12%] w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(254,210,2,0.18) 0%, transparent 65%)' }}
      />
      <motion.div
        animate={{ scale: [1.1, 1, 1.1], x: [0, -20, 0], y: [0, 20, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        className="absolute bottom-[10%] left-[5%] w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(254,210,2,0.12) 0%, transparent 65%)' }}
      />
      <motion.div
        animate={{ scale: [1, 1.3, 1], y: [0, -40, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 4 }}
        className="absolute top-[40%] left-[30%] w-[300px] h-[300px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(254,210,2,0.06) 0%, transparent 65%)' }}
      />

      {/* Grid overlay */}
      <div className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{ backgroundImage: 'linear-gradient(#0B0B0B 1px,transparent 1px),linear-gradient(90deg,#0B0B0B 1px,transparent 1px)', backgroundSize: '64px 64px' }} />

      {/* Floating particles */}
      {[...Array(9)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-primary/50"
          style={{
            width: 4 + (i % 3) * 2,
            height: 4 + (i % 3) * 2,
            left: `${8 + i * 10}%`,
            top: `${15 + (i % 4) * 20}%`,
          }}
          animate={{ y: [-18, 18, -18], opacity: [0.15, 0.7, 0.15], scale: [1, 1.4, 1] }}
          transition={{ duration: 3 + i * 0.6, repeat: Infinity, delay: i * 0.3 }}
        />
      ))}

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-24 w-full flex-1 flex items-center">
        <div className="grid lg:grid-cols-2 gap-16 items-center w-full">

          {/* Left */}
          <motion.div style={{ y: textY, opacity }}>
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full glass-yellow text-foreground text-sm font-semibold mb-8 shadow-md shadow-primary/15"
            >
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}>
                <Sparkles className="w-4 h-4 text-primary" />
              </motion.div>
              India's #1 Tech Career Platform
              <motion.span
                animate={{ scale: [1, 1.5, 1], opacity: [0.6, 1, 0.6] }}
                transition={{ duration: 1.4, repeat: Infinity }}
                className="w-2 h-2 rounded-full bg-primary"
              />
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              style={{fontFamily: 'sans-serif'}}
              className="font-heading text-5xl sm:text-6xl lg:text-[3.6rem] font-bold leading-[1.03] text-foreground"
            >
              We Don't Just
              <br />
              Teach {' '}<span>   We </span>
              <span className="relative">
             
                <AnimatePresence className=' inline-block' mode="wait">
                  <motion.span
                    key={wordIndex}
                    initial={{ opacity: 0, y: 30, rotateX: -20 }}
                    animate={{ opacity: 1, y: 0, rotateX: 0 }}
                    exit={{ opacity: 0, y: -30, rotateX: 20 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="text-primary inline-block"
                    style={{ display: 'inline-block' }}
                  >
                    {words[wordIndex]}
                  </motion.span>
                </AnimatePresence>
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 1.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute -bottom-2 left-0 right-0 h-[3px] bg-primary rounded-full origin-left"
                />
              </span>
              <br />
              <span className="text-foreground/25">Careers</span>
            </motion.h1>

            {/* Sub */}
            <motion.p
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.7 }}
              className="mt-7 text-xl text-muted-foreground leading-relaxed max-w-lg font-body"
            >
              Industry mentors, real-time projects, and dedicated placement support, {' '}
              <span className="font-semibold text-foreground"> until you get hired.</span>
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap gap-4 mt-10"
            >
              <Link to="/courses">
                <motion.div whileHover={{ scale: 1.06, y: -3 }} whileTap={{ scale: 0.96 }}>
                  <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2.5 px-9 py-4 h-auto text-base font-bold shadow-2xl shadow-primary/35 rounded-2xl">
                    Explore Courses
                    <motion.span animate={{ x: [0, 6, 0] }} transition={{ duration: 1.4, repeat: Infinity }}>
                      <ArrowRight className="w-5 h-5" />
                    </motion.span>
                  </Button>
                </motion.div>
              </Link>
              <Link to="/contact">
                <motion.div whileHover={{ scale: 1.06, y: -3 }} whileTap={{ scale: 0.96 }}>
                  <Button variant="outline" size="lg" className="glass border-border/50 text-foreground gap-2.5 px-9 py-4 h-auto text-base font-semibold rounded-2xl hover:bg-white/90">
                    <Phone className="w-4 h-4 text-primary" />
                    Talk to Mentor
                  </Button>
                </motion.div>
              </Link>
            </motion.div>

            {/* Trust pills */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="flex flex-wrap gap-2.5 mt-8"
            >
              {['Industry Mentors', 'Real-Time Projects', 'Placement Support'].map((label, i) => (
                <motion.span
                  key={label}
                  initial={{ opacity: 0, x: -15 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9 + i * 0.1 }}
                  whileHover={{ scale: 1.06, y: -2 }}
                  className="text-xs px-4 py-2 rounded-full glass border-primary/20 text-foreground font-semibold cursor-default"
                >
                  ✓ {label}
                </motion.span>
              ))}
            </motion.div>

            {/* Social proof */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="flex items-center gap-5 mt-9 pt-7 border-t border-black/6"
            >
              <div className="flex -space-x-3">
                {[
                  'https://xceededge.com/data/images/U.VINEETHA.jpeg',
                  'https://xceededge.com/data/images/T.%20NEERAJA.jpeg',
                  'https://xceededge.com/data/images/Y.Dharmendra.jpeg',
                  'https://xceededge.com/data/images/G.Sai-Madhav.jpeg',
                  'https://xceededge.com/data/images/SHAIK-MUHAMMAD-IRFAN.jpeg',
                ].map((src, i) => (
                  <motion.img
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.1 + i * 0.07 }}
                    src={src} alt="student"
                    className="w-10 h-10 rounded-full border-2 border-white object-cover shadow-md"
                  />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  {[1,2,3,4,5].map(s => (
                    <motion.div key={s} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.3 + s * 0.06, type: 'spring', stiffness: 400 }}>
                      <Star className="w-4 h-4 fill-primary text-primary" />
                    </motion.div>
                  ))}
                  <span className="text-sm font-bold text-foreground ml-1.5">4.9</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Trusted by <span className="font-bold text-foreground">5,000+ students</span>
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Images + floating badges */}
          <motion.div
            initial={{ opacity: 0, x: 80 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block relative h-[580px]"
          >
            {/* Main image */}
            <motion.div
              whileHover={{ scale: 1.02, rotate: -0.5 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="absolute top-0 right-0 w-[83%] h-[380px] rounded-3xl overflow-hidden s border border-white/80"
            >
              <img
                // src="https://images.unsplash.com/photo-1531482615713-2afd69097998?w=900&q=80"
                src="https://xceededge.com/data/images/young-person-intership.jpg"
                alt="students learning"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/35 to-transparent" />
              {/* Play button overlay */}
              <motion.div
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.9 }}
                className="absolute inset-0 flex items-center justify-center cursor-pointer"
              >
                {/* <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2.5, repeat: Infinity }}
                  className="w-16 h-16 rounded-full bg-white/30 backdrop-blur-sm border border-white/60 flex items-center justify-center shadow-xl"
                >
                  <Play className="w-6 h-6 text-white fill-white ml-1" />
                </motion.div> */}
              </motion.div>
            </motion.div>

            {/* Secondary image */}
            <motion.div
              animate={{ y: [-6, 6, -6] }}
              transition={{ duration: 5.5, repeat: Infinity }}
              className="absolute bottom-0 left-0 w-[54%] h-[250px] rounded-3xl overflow-hidden shadow-xl shadow-black/12 border border-white/80"
            >
              <img
                src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&q=80"
                alt="coding"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
              <div className="absolute bottom-5 left-4">
                <p className="text-white text-sm font-bold">Live Class in Progress</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <motion.div
                    animate={{ scale: [1, 1.5, 1], opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 1.2, repeat: Infinity }}
                    className="w-2.5 h-2.5 rounded-full bg-primary"
                  />
                  <span className="text-primary text-xs font-semibold">247 watching</span>
                </div>
              </div>
            </motion.div>

            {/* Floating badges */}
            {floatingBadges.map(({ icon: BadgeIcon, label, pos, delay }) => (
              <motion.div
                key={label}
                className={`absolute ${pos} glass rounded-2xl px-4 py-3 flex items-center gap-3 shadow-lg shadow-black/6`}
                animate={{ y: [-7, 7, -7] }}
                transition={{ duration: 3.5 + delay, repeat: Infinity, delay }}
                whileHover={{ scale: 1.1 }}
              >
                <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center flex-shrink-0 shadow-md shadow-primary/30">
                  <BadgeIcon className="w-4 h-4 text-primary-foreground" />
                </div>
                <span className="text-foreground text-xs font-bold whitespace-nowrap">{label}</span>
              </motion.div>
            ))}

            {/* Salary hike badge */}
            <motion.div
              animate={{ y: [5, -5, 5] }}
              transition={{ duration: 4.5, repeat: Infinity, delay: 1 }}
              className="absolute top-[38%] -left-10 glass rounded-2xl shadow-xl shadow-black/10 px-4 py-3.5 flex items-center gap-3"
            >
              <div className="w-11 h-11 bg-primary/15 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-foreground font-bold text-lg leading-none font-heading">₹4.5 LPA</p>
                <p className="text-muted-foreground text-xs mt-0.5">Avg. Salary Hike</p>
              </div>
            </motion.div>

            {/* New enrollment popup */}
            {/* <motion.div
              animate={{ y: [-8, 8, -8] }}
              transition={{ duration: 6, repeat: Infinity, delay: 2 }}
              className="absolute top-[5%] left-[4%] glass rounded-full shadow-lg px-4 py-2.5 flex items-center gap-2.5"
            >
              <motion.div
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <CheckCircle className="w-4 h-4 text-primary" />
              </motion.div>
              <span className="text-foreground text-xs font-semibold">New student enrolled!</span>
            </motion.div> */}
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
      >
        <span className="text-[10px] text-muted-foreground font-semibold tracking-widest uppercase">Scroll</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.4, repeat: Infinity }}>
          <ChevronDown className="w-5 h-5 text-muted-foreground/50" />
        </motion.div>
      </motion.div>

      <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-background to-transparent pointer-events-none" />
    </section>
  );
}